/**
 * saleAllocationEditSameDoApi — RTK Query slice for the Sale Allocation
 * "Edit (similar DO)" bulk-edit grid (Access `frmCtrlSalesQrySaleAlc_Edit_SameDO`
 * hosting subform `frmCtrlSalesQrySaleAlc_TickCont`).
 *
 * Access flow (btnEditMany_Click): clears tmp `public_tmp_sales_alc_tick`, then
 * `INSERT … SELECT *, 1 AS checked_row FROM public_v_sales_allocation_scs
 *  WHERE delivery_order = '<do_no>'` and opens the grid. btnSave_Click loops the
 * ticked rows and `UPDATE public_sales_allocation_scs SET receipt_no, allocating_bag,
 * allocating_quantity, updated_*` for each.
 *
 * → On the web there is no tmp table: we fetch the rows for one DO, edit the three
 *   editable columns inline (receipt_no / allocating_bag / allocating_quantity) per
 *   ticked row, and PUT the ticked rows back in one batch.
 *
 * BE NOT BUILT YET — endpoints below are declared so the dialog can wire its hooks;
 * until the backend ships they 404 and the grid guards with `data?.rows ?? []`
 * (no fabricated rows). See TODO markers per endpoint.
 *
 * Reuses the shared `SaleControl` cache tag so the bulk update invalidates the
 * Sale Allocation list (Access `…!subForm.Requery`).
 */
import { apiSlice } from './apiSlice'

/** One row of `public_v_sales_allocation_scs` for a given delivery_order (camelCase). */
export interface SaleAllocationSameDoRow {
  /** sales_allocation_scs_id — PK targeted by the UPDATE. */
  salesAllocationScsId: number
  /** receipt_no — editable. */
  receiptNo: string | null
  /** allocating_bag — editable (Format Standard). */
  allocatingBag: number | null
  /** allocating_quantity — editable (Format Standard). */
  allocatingQuantity: number | null
  // ── Display-only context columns (TickCont datasheet order by Top) ──
  containerNo: string | null
  mDate: string | null
  mTruckNo: string | null
  mNetWeight: number | null
  saleContract: string | null
  deliveryOrder: string | null
  bookingNo: string | null
  contractQuality: string | null
  fromWarehouse: string | null
}

export interface SaleAllocationSameDoResponse {
  rows: SaleAllocationSameDoRow[]
}

/** One ticked row's editable payload for the batch UPDATE. */
export interface SaleAllocationSameDoUpdateItem {
  salesAllocationScsId: number
  receiptNo: string | null
  allocatingBag: number | null
  allocatingQuantity: number | null
}

export interface SaleAllocationSameDoUpdateRequest {
  rows: SaleAllocationSameDoUpdateItem[]
}

const saleAllocationEditSameDoApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // TODO BE: GET /sale-controls/sale-alc/same-do?doNo=<delivery_order>
    //   SELECT … FROM public_v_sales_allocation_scs WHERE delivery_order = :doNo.
    //   (Access btnEditMany INSERT … SELECT into tmp_sales_alc_tick.)
    getSaleAllocationsSameDo: builder.query<SaleAllocationSameDoResponse, string>({
      query: (doNo) => ({ url: '/sale-controls/sale-alc/same-do', params: { doNo } }),
      keepUnusedDataFor: 0,
    }),
    // TODO BE: PUT /sale-controls/sale-alc/same-do — batch UPDATE the ticked rows.
    //   For each row: UPDATE public_sales_allocation_scs SET receipt_no,
    //   allocating_bag, allocating_quantity, updated_date, updated_by
    //   WHERE sales_allocation_scs_id = :id AND is_status = 0.
    updateSaleAllocationsSameDo: builder.mutation<{ updated: number }, SaleAllocationSameDoUpdateRequest>({
      query: (body) => ({ url: '/sale-controls/sale-alc/same-do', method: 'PUT', body }),
      invalidatesTags: ['SaleControl'],
    }),
  }),
})

export const {
  useGetSaleAllocationsSameDoQuery,
  useUpdateSaleAllocationsSameDoMutation,
} = saleAllocationEditSameDoApi

export default saleAllocationEditSameDoApi
